import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { tenderAPI } from '../services/api';

/**
 * 產品價格冊公開頁面
 * 透過加密 token 識別經銷商身份，不需登入
 * URL: /prices/{token}
 */
function PriceCatalogPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await tenderAPI.getPriceCatalog(token);
      setData(res.data);
    } catch (err) {
      setError('連結無效或已過期');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={{ textAlign: 'center', fontSize: 18, padding: 60 }}>載入中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ textAlign: 'center', padding: 60 }}>
            <h2 style={{ color: '#c62828' }}>{error}</h2>
            <p style={{ color: '#666', marginTop: 12 }}>
              如有問題請洽 周伶瑋 (Jolie) 02-8773-0263 #181
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { campaign, dealer, products } = data;

  // Group products by brand
  const groupedByBrand = {};
  (products || []).forEach((p) => {
    const brand = p.brand_name || '其他';
    if (!groupedByBrand[brand]) {
      groupedByBrand[brand] = [];
    }
    groupedByBrand[brand].push(p);
  });

  const brandOrder = ['Comboware', 'GraphOn'];
  const sortedBrands = Object.keys(groupedByBrand).sort((a, b) => {
    const ia = brandOrder.indexOf(a);
    const ib = brandOrder.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>宇聯科技股份有限公司</h1>
          <p style={styles.headerSub}>{campaign.case_number} 產品公告底價清單</p>
        </div>

        <div style={styles.body}>
          {/* Info Section */}
          <div style={styles.infoBox}>
            <div style={styles.infoGrid}>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>案號</span>
                <span style={styles.infoValue}>{campaign.case_number}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>案名</span>
                <span style={styles.infoValue}>{campaign.title}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>經銷商</span>
                <span style={styles.infoValue}>{dealer.company}</span>
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>聯絡人</span>
                <span style={styles.infoValue}>{dealer.name}</span>
              </div>
            </div>
          </div>

          {/* Product Tables grouped by brand */}
          {sortedBrands.map((brand) => {
            const brandProducts = groupedByBrand[brand];
            return (
              <div key={brand} style={styles.brandSection}>
                <div style={styles.brandHeader}>
                  <h3 style={styles.brandTitle}>{brand}</h3>
                  <span style={styles.brandCount}>{brandProducts.length} 項產品</span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.table}>
                    <thead>
                      <tr style={styles.tableHeaderRow}>
                        <th style={{ ...styles.th, width: 50, textAlign: 'center' }}>組別</th>
                        <th style={{ ...styles.th, width: 50, textAlign: 'center' }}>項次</th>
                        <th style={{ ...styles.th, minWidth: 200 }}>品名</th>
                        <th style={{ ...styles.th, width: 90, textAlign: 'center' }}>單套授權數</th>
                        <th style={{ ...styles.th, width: 70, textAlign: 'center' }}>產品單位</th>
                        <th style={{ ...styles.th, width: 100, textAlign: 'center' }}>數量級距</th>
                        <th style={{ ...styles.th, width: 130, textAlign: 'right' }}>公告底價含稅</th>
                        <th style={{ ...styles.th, width: 60, textAlign: 'center' }}>產地</th>
                        <th style={{ ...styles.th, width: 90, textAlign: 'center' }}>原廠或代理</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brandProducts.map((p, idx) => (
                        <tr
                          key={idx}
                          style={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}
                        >
                          <td style={{ ...styles.td, textAlign: 'center' }}>{p.group_number || '-'}</td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>{p.item_number}</td>
                          <td style={styles.td}>{p.product_name}</td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>{p.unit_licenses || '-'}</td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>{p.unit || '-'}</td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>{p.quantity_range || '-'}</td>
                          <td style={styles.priceCell}>
                            {p.price != null ? `NT$ ${p.price.toLocaleString()}` : '-'}
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>{p.origin || '-'}</td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>{p.agent_type || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          {/* Notes */}
          <div style={styles.notesBox}>
            <h4 style={styles.notesTitle}>備註</h4>
            <ol style={styles.notesList}>
              <li>上述公告底價皆含稅</li>
              <li>上述報價皆含一年保固服務</li>
              <li>上述報價皆只含軟體授權，未含安裝系統所需之硬體</li>
            </ol>
          </div>

          {/* Contact */}
          <div style={styles.contactBox}>
            <h4 style={styles.contactTitle}>聯絡窗口</h4>
            <div style={styles.contactGrid}>
              <div style={styles.contactCard}>
                <p style={styles.contactName}>周伶瑋 (Jolie)</p>
                <p style={styles.contactDetail}>02-8773-0263 #181</p>
                <p style={styles.contactDetail}>jolie.chou@ulink.com.tw</p>
              </div>
              <div style={styles.contactCard}>
                <p style={styles.contactName}>蘇鈺涵 (Anny)</p>
                <p style={styles.contactDetail}>02-8773-0263 #182</p>
                <p style={styles.contactDetail}>anny.su@ulink.com.tw</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <p>宇聯科技股份有限公司 ULink Technology Inc.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f0f2f5',
    padding: '20px',
    fontFamily: "'Microsoft JhengHei', 'Noto Sans TC', Arial, sans-serif",
  },
  card: {
    maxWidth: 1100,
    margin: '0 auto',
    background: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
  },
  header: {
    background: 'linear-gradient(135deg, #b71c1c, #e53935)',
    padding: '36px 30px',
    textAlign: 'center',
  },
  headerTitle: {
    color: '#fff',
    margin: 0,
    fontSize: 26,
    letterSpacing: 2,
  },
  headerSub: {
    color: '#ffcdd2',
    margin: '10px 0 0',
    fontSize: 16,
    letterSpacing: 1,
  },
  body: {
    padding: '30px',
  },
  infoBox: {
    background: '#fafafa',
    padding: 20,
    borderRadius: 6,
    marginBottom: 28,
    border: '1px solid #e0e0e0',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: 12,
  },
  infoItem: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
  },
  infoLabel: {
    fontSize: 13,
    color: '#888',
    minWidth: 50,
    flexShrink: 0,
  },
  infoValue: {
    fontSize: 15,
    fontWeight: 600,
    color: '#333',
  },
  brandSection: {
    marginBottom: 32,
  },
  brandHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    borderBottom: '2px solid #c62828',
    paddingBottom: 8,
  },
  brandTitle: {
    margin: 0,
    fontSize: 18,
    color: '#c62828',
    fontWeight: 700,
  },
  brandCount: {
    fontSize: 13,
    color: '#999',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    border: '1px solid #e0e0e0',
    fontSize: 13,
  },
  tableHeaderRow: {
    background: '#f5f5f5',
  },
  th: {
    padding: '10px 8px',
    textAlign: 'left',
    borderBottom: '2px solid #ccc',
    fontSize: 12,
    fontWeight: 700,
    color: '#555',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '8px',
    borderBottom: '1px solid #eee',
    fontSize: 13,
    color: '#333',
  },
  priceCell: {
    padding: '8px',
    borderBottom: '1px solid #eee',
    fontSize: 14,
    textAlign: 'right',
    fontWeight: 'bold',
    color: '#c62828',
    whiteSpace: 'nowrap',
  },
  rowEven: {},
  rowOdd: {
    background: '#fafafa',
  },
  notesBox: {
    background: '#fff8e1',
    padding: 20,
    borderRadius: 6,
    marginBottom: 24,
    borderLeft: '4px solid #f9a825',
  },
  notesTitle: {
    margin: '0 0 10px',
    fontSize: 15,
    color: '#e65100',
  },
  notesList: {
    margin: 0,
    paddingLeft: 20,
    lineHeight: 2,
    fontSize: 14,
    color: '#555',
  },
  contactBox: {
    marginBottom: 24,
  },
  contactTitle: {
    margin: '0 0 12px',
    fontSize: 15,
    color: '#333',
  },
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16,
  },
  contactCard: {
    background: '#f5f5f5',
    padding: 16,
    borderRadius: 6,
  },
  contactName: {
    margin: '0 0 6px',
    fontWeight: 700,
    fontSize: 15,
    color: '#333',
  },
  contactDetail: {
    margin: '0 0 4px',
    fontSize: 13,
    color: '#666',
  },
  footer: {
    textAlign: 'center',
    paddingTop: 20,
    borderTop: '1px solid #eee',
    fontSize: 12,
    color: '#aaa',
  },
};

export default PriceCatalogPage;
